import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// تعريف OneSignal للتوافق مع TypeScript
declare global {
    interface Window {
        OneSignal: any;
        OneSignalDeferred: any[];
    }
}

// Configure axios instance
const api = axios.create({
    baseURL: 'https://taj.city',
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// Add response interceptor
api.interceptors.response.use(
    (response) => response,
    (error) => {
        console.error('API Error:', error.response?.data || error.message);
        throw error;
    }
);

interface User {
    id: number;
    email: string;
    phone: string;
    token: string;
}

interface AuthContextType {
    user: User | null;
    login: (email: string, password: string) => Promise<void>;
    register: (email: string, password: string, phone: string) => Promise<void>;
    logout: () => void;
    isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            try {
                const parsedUser = JSON.parse(storedUser);
                setUser(parsedUser);
                setIsAuthenticated(true);
            } catch (error) {
                console.error('Error parsing stored user:', error);
                localStorage.removeItem('user');
            }
        }
    }, []);

    const login = async (email: string, password: string) => {
        try {
            const response = await api.post('/backend/api/auth/login.php', {
                email,
                password
            });

            if (response.data && response.data.token) {
                const userData: User = {
                    id: response.data.id,
                    email: response.data.email,
                    phone: response.data.phone || '',
                    token: response.data.token
                };
                setUser(userData);
                setIsAuthenticated(true);
                localStorage.setItem('user', JSON.stringify(userData));
                
                // إطلاق حدث تسجيل الدخول لتفعيل الإشعارات
                document.dispatchEvent(new Event('userLoggedIn'));

                // ربط معرف المستخدم مع OneSignal إذا كان متاحًا
                if (window.OneSignal) {
                    try {
                        await window.OneSignal.login(userData.id.toString());
                        //console.log('User ID linked with OneSignal:', userData.id);
                    } catch (error) {
                        console.error('Error linking user ID with OneSignal:', error);
                    }
                }
            } else {
                throw new Error('Invalid response format');
            }
        } catch (error) {
            console.error('Login error:', error);
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.error || error.response?.data?.message || error.message);
            }
            throw error;
        }
    };

    const register = async (email: string, password: string, phone: string) => {
        try {
            //console.log('Sending registration data:', { email, password, phone });

            const response = await api.post('/backend/api/auth/register.php', {
                email,
                password,
                phone
            });

            //console.log('Registration response:', response.data);

            if (response.data && response.data.token) {
                const userData: User = {
                    id: response.data.id,
                    email: response.data.email,
                    phone: response.data.phone || '',
                    token: response.data.token
                };
                setUser(userData);
                setIsAuthenticated(true);
                localStorage.setItem('user', JSON.stringify(userData));
                
                // إطلاق حدث تسجيل الدخول لتفعيل الإشعارات
                document.dispatchEvent(new Event('userLoggedIn'));

                // ربط معرف المستخدم مع OneSignal إذا كان متاحًا
                if (window.OneSignal) {
                    try {
                        await window.OneSignal.login(userData.id.toString());
                        //console.log('User ID linked with OneSignal:', userData.id);
                    } catch (error) {
                        console.error('Error linking user ID with OneSignal:', error);
                    }
                }
            } else {
                throw new Error('Invalid response format');
            }
        } catch (error) {
            console.error('Registration error:', error);
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.error || error.response?.data?.message || error.message);
            }
            throw error;
        }
    };

    const logout = () => {
        setUser(null);
        setIsAuthenticated(false);
        localStorage.removeItem('user');
        
        // إزالة ربط معرف المستخدم مع OneSignal عند تسجيل الخروج
        if (window.OneSignal) {
            try {
                window.OneSignal.logout();
                //console.log('User ID unlinked from OneSignal');
            } catch (error) {
                console.error('Error unlinking user ID from OneSignal:', error);
            }
        }
    };

    return (
        <AuthContext.Provider value={{ user, login, register, logout, isAuthenticated }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}; 